import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAdminCardVoucher, IVouchersImportXls } from 'types/income';
import { exportXlsxConfig, getFormData } from 'utils/general';
import { Currency, TableDataResponse } from 'types/general';
import { RedeemMethods, VoucherStatus } from 'store/vouchers';
import { AdminCardVouchersFilters } from './admin-card-vouchers.types';

interface GetCardVouchers {
  cardId: string;
  filters: Partial<AdminCardVouchersFilters>;
}

interface GetCardVoucher {
  voucherId: string;
}

interface UpdateCardVoucher {
  voucherId: string;
  data: Partial<
    {
      cardId: string;
      cardPrice: number;
      status: VoucherStatus;
      purchasePrice: number;
      purchaseReference: string;
      shopItemPriceId: string;
      shopOrderId: string;
      expiresAt: string | null;
    } & RedeemMethods
  >;
}

interface ExportVouchersXls {
  voucherIds: string[];
}

interface ImportVouchersXls {
  cardId: string;
  cardPrice: string;
  cardCurrency: string;
  file: File;
}

interface ImportVouchersFiles {
  cardId: string;
  purchasePrice: number;
  purchaseReference: string;
  cardPrice: number;
  cardCurrency: Currency;
  expiresAt: string | null;
  files: File[];
}

interface UpdateStatus {
  vouchers: string[];
  status: VoucherStatus;
}

export const loaders = {
  getCardVouchers: ({ cardId, filters }: GetCardVouchers) => {
    return api<TableDataResponse<IAdminCardVoucher>>({
      url: Endpoints.melpAdmin.cards.vouchers.root.replace(':cardId', cardId),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search || undefined,
        filter: {
          status: filters.status,
          faceValues: filters.prices,
        },
      },
    });
  },
  getCardVoucher: ({ voucherId }: GetCardVoucher) => {
    return api<IAdminCardVoucher>({
      url: Endpoints.melpAdmin.cards.vouchers.byId.replace(
        ':voucherId',
        voucherId,
      ),
    });
  },
  updateCardVoucher: ({ voucherId, data }: UpdateCardVoucher) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.byId.replace(
        ':voucherId',
        voucherId,
      ),
      method: 'patch',
      data: {
        cardId: data.cardId,
        faceValue: data.cardPrice,
        status: data.status,
        qrCode: data.qrCode,
        barCode: data.barCode,
        serial: data.serial,
        url: data.url,
        code: data.code,
        pin: data.pin,
        purchasePrice: data.purchasePrice,
        purchaseReference: data.purchaseReference,
        marketplaceOrderId: data.shopOrderId,
        expirationDate: data.expiresAt,
      },
    });
  },
  updateStatus: ({ vouchers, status }: UpdateStatus) => {
    return api({
      url: Endpoints.melpAdmin.cards.vouchers.status,
      method: 'patch',
      data: {
        voucherIds: vouchers,
        status,
      },
    });
  },
  exportXlsTemplate: () => {
    return api({
      url: Endpoints.melpAdmin.cards.vouchers.import.xls,
      ...exportXlsxConfig,
    });
  },
  exportVouchersXls: ({ voucherIds }: ExportVouchersXls) => {
    return api({
      url: Endpoints.melpAdmin.cards.vouchers.export.xls,
      params: {
        voucherIds,
      },
      ...exportXlsxConfig,
    });
  },
  importVouchersXls: ({
    cardId,
    cardPrice,
    cardCurrency,
    file,
  }: ImportVouchersXls) => {
    return api<IVouchersImportXls>({
      url: Endpoints.melpAdmin.cards.vouchers.import.xls,
      method: 'post',
      data: getFormData([
        { field: 'file', value: file },
        { field: 'cardId', value: cardId },
        { field: 'faceValue', value: cardPrice },
        { field: 'faceValueCurrency', value: cardCurrency },
      ]),
    });
  },
  importVouchersFiles: ({
    cardId,
    cardPrice,
    cardCurrency,
    purchasePrice,
    purchaseReference,
    expiresAt,
    files,
  }: ImportVouchersFiles) => {
    return api({
      url: Endpoints.melpAdmin.cards.vouchers.import.files,
      method: 'post',
      data: getFormData([
        ...files.map((file) => ({ field: 'files', value: file })),
        { field: 'cardId', value: cardId },
        { field: 'purchaseValue', value: String(purchasePrice) },
        { field: 'faceValue', value: String(cardPrice) },
        { field: 'faceValueCurrency', value: cardCurrency },
        { field: 'purchaseReference', value: purchaseReference },
        ...(expiresAt ? [{ field: 'expirationDate', value: expiresAt }] : []),
      ]),
    });
  },
};
